import React, { useState } from 'react';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import useInView from '../hooks/useInView';

const STORIES = [
  {
    metric: '80%',
    metricLabel: 'lower SMS cost',
    quote: 'We run a fleet of 50+ trucks across the GTA and needed a reliable way to push status updates to freight forwarders and clients without paying through the nose. TextLink let us plug in a spare Android, connect our SIM, and we were live the same afternoon.',
    name: 'Paul Bhangal',
    role: 'President & CEO',
    company: "Paul's Transport"
  },
  {
    metric: '<5 min',
    metricLabel: 'from signup to first send',
    quote: 'Before TextLink we were losing leads in the gap between ad click and first contact. Now our SIM-based follow-ups go out within minutes and conversion to booked appointments has measurably improved.',
    name: 'Grzegorz Lewna',
    role: 'CEO',
    company: 'SolarExclusive'
  },
  {
    metric: '0',
    metricLabel: 'A2P forms filled out',
    quote: 'TextLink killed the A2P registration nightmare and gave me real carrier-grade delivery. The REST API integrated cleanly with my automation stack in a couple of hours.',
    name: 'Russ Ward',
    role: 'Founder',
    company: 'The Lead King'
  }
];

export default function HomeMetrics() {
  const [i, setI] = useState(0);
  const [ref, inView] = useInView();
  const s = STORIES[i];

  const prev = () => setI((x) => (x - 1 + STORIES.length) % STORIES.length);
  const next = () => setI((x) => (x + 1) % STORIES.length);

  return (
    <section ref={ref} className={`hm${inView ? ' in-view' : ''}`}>
      <div className="hm-container">
        <header className="hm-head">
          <div className="hm-title-block">
            <h2 className="hm-title">Teams that grow with TextLink</h2>
          </div>
          <div className="hm-nav">
            <button className="hm-nav-btn" onClick={prev} aria-label="Previous story">
              <LuArrowLeft size={16} strokeWidth={2} />
            </button>
            <button className="hm-nav-btn" onClick={next} aria-label="Next story">
              <LuArrowRight size={16} strokeWidth={2} />
            </button>
          </div>
        </header>

        <article className="hm-feature" key={i}>
          <div className="hm-feature-visual">
            <div className="hm-metric-stack">
              <div className="hm-metric-big">{s.metric}</div>
              <div className="hm-metric-label">{s.metricLabel}</div>
              <div className="hm-company-tag">{s.company}</div>
            </div>
          </div>
          <div className="hm-feature-body">
            <blockquote className="hm-quote">
              <span className="hm-quote-mark">&ldquo;</span>
              {s.quote}
            </blockquote>
            <footer className="hm-author">
              <div className="hm-avatar">{s.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}</div>
              <div>
                <div className="hm-name">{s.name}</div>
                <div className="hm-role">{s.role} &middot; {s.company}</div>
              </div>
            </footer>
          </div>
        </article>

        <div className="hm-dots" role="tablist" aria-label="Customer stories">
          {STORIES.map((_, idx) => (
            <button
              key={idx}
              className={`hm-dot${idx === i ? ' hm-dot--active' : ''}`}
              onClick={() => setI(idx)}
              aria-label={`Story ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        .hm {
          background: #ffffff;
          padding: clamp(6rem, 14vh, 11rem) clamp(1rem, 3vw, 2rem);
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #000;
          -webkit-font-smoothing: antialiased;
        }
        .hm-container { max-width: 1200px; margin: 0 auto; }

        .hm-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 2rem;
          margin-bottom: clamp(2.5rem, 5vh, 3.5rem);
          flex-wrap: wrap;
        }
        .hm-title-block { display: flex; align-items: baseline; gap: 1.25rem; flex-wrap: wrap; }
        .hm-title {
          font-size: clamp(2.25rem, 5.5vw, 3.75rem);
          font-weight: 600;
          letter-spacing: -0.045em;
          line-height: 1.02;
          margin: 0;
        }
        .hm-viewall {
          font-size: 0.9375rem;
          color: rgba(0,0,0,0.55);
          text-decoration: none;
          font-weight: 500;
          transition: color 160ms ease;
        }
        .hm-viewall:hover { color: #000; }

        .hm-nav { display: flex; gap: 0.5rem; }
        .hm-nav-btn {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: 1px solid rgba(0,0,0,0.12);
          background: #ffffff;
          color: #000;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 160ms ease, border-color 160ms ease;
        }
        .hm-nav-btn:hover { background: #fafafa; border-color: rgba(0,0,0,0.28); }

        .hm-feature {
          display: grid;
          grid-template-columns: 0.85fr 1.15fr;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: 20px;
          overflow: hidden;
          min-height: 340px;
          animation: hm-fade 500ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes hm-fade {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hm-head, .hm-feature, .hm-dots {
          opacity: 0;
          translate: 0 16px;
          transition: opacity 650ms cubic-bezier(0.16, 1, 0.3, 1), translate 650ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hm.in-view .hm-head { opacity: 1; translate: 0 0; }
        .hm.in-view .hm-feature { opacity: 1; translate: 0 0; transition-delay: 120ms; }
        .hm.in-view .hm-dots { opacity: 1; translate: 0 0; transition-delay: 240ms; }
        @media (max-width: 800px) { .hm-feature { grid-template-columns: 1fr; } }

        .hm-feature-visual {
          padding: clamp(2rem, 3.5vw, 3rem);
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 300px;
          position: relative;
          overflow: hidden;
          background: linear-gradient(160deg, #0a0a0a 0%, #1a102e 60%, #2e1065 100%);
        }
        .hm-feature-visual::before {
          content: "";
          position: absolute;
          inset: -40% -10% auto -10%;
          height: 80%;
          background:
            radial-gradient(ellipse at 30% 40%, rgba(168, 85, 247, 0.45), transparent 55%),
            radial-gradient(ellipse at 75% 60%, rgba(124, 58, 237, 0.3), transparent 55%);
          pointer-events: none;
          filter: blur(6px);
          animation: hm-glow 10s ease-in-out infinite;
        }
        @keyframes hm-glow {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(-8px, 6px); }
        }
        @media (max-width: 800px) { .hm-feature-visual { min-height: 240px; } }

        .hm-metric-stack {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }

        .hm-metric-big {
          font-size: clamp(3.25rem, 7vw, 5.25rem);
          font-weight: 700;
          letter-spacing: -0.055em;
          line-height: 0.95;
          color: #ffffff;
          text-shadow: 0 2px 16px rgba(0,0,0,0.5);
        }
        .hm-metric-label {
          font-size: 1rem;
          color: rgba(255,255,255,0.85);
          font-weight: 500;
          letter-spacing: -0.005em;
        }
        .hm-company-tag {
          margin-top: 1.25rem;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #c4b5fd;
        }

        .hm-feature-body {
          padding: clamp(2rem, 3.5vw, 3rem);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 1.75rem;
        }
        .hm-quote {
          font-size: clamp(1.0625rem, 1.7vw, 1.25rem);
          line-height: 1.55;
          color: rgba(0,0,0,0.85);
          font-weight: 400;
          margin: 0;
          letter-spacing: -0.01em;
          position: relative;
        }
        .hm-quote-mark {
          font-size: 2.5rem;
          color: #7c3aed;
          line-height: 0;
          font-weight: 600;
          margin-right: 0.15rem;
          position: relative;
          top: 0.5rem;
        }

        .hm-author { display: flex; align-items: center; gap: 0.875rem; }
        .hm-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #0a0a0a;
          color: #fff;
          font-size: 0.8125rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .hm-name { font-weight: 600; font-size: 0.9375rem; letter-spacing: -0.01em; }
        .hm-role { font-size: 0.8125rem; color: rgba(0,0,0,0.55); margin-top: 0.125rem; }

        .hm-dots {
          display: flex;
          justify-content: center;
          gap: 0.4rem;
          margin-top: clamp(1.5rem, 3vh, 2rem);
        }
        .hm-dot {
          width: 28px;
          height: 4px;
          border-radius: 999px;
          background: rgba(0,0,0,0.12);
          border: none;
          cursor: pointer;
          padding: 0;
          transition: background 200ms ease, width 200ms ease;
        }
        .hm-dot--active { background: #0a0a0a; width: 40px; }
      `}</style>
    </section>
  );
}
